import { FormControlLabel, Radio, RadioGroup, Select, Stack, TextField } from '@mui/material';
import { ColDef, DoesFilterPassParams } from 'ag-grid-community';
import { CustomFilterDisplayProps } from 'ag-grid-react';
import { FC, useEffect, useMemo } from 'react';
import { Nullable } from 'src/domain/types/nullable';

type CellValue = Nullable<string | number>;

const allFilters = [
    {
        displayKey: 'contains',
        displayName: 'Contains',
        predicate: (filterValue: Nullable<string>, cellValue: CellValue) => {
            if (!filterValue) return true;
            return (cellValue ?? '').toString().toLowerCase().includes(filterValue.toLowerCase());
        },
    },
    {
        displayKey: 'notContains',
        displayName: 'Does not contain',
        predicate: (filterValue: Nullable<string>, cellValue: CellValue) => {
            if (filterValue === null || filterValue === undefined) return true;
            return !(cellValue ?? '').toString().toLowerCase().includes(filterValue.toLowerCase());
        },
    },
    {
        displayKey: 'equals',
        displayName: 'Equals',
        predicate: (filterValue: Nullable<string>, cellValue: CellValue) => {
            if (!filterValue) return true;
            return filterValue.toLowerCase() === (cellValue ?? '').toString().toLowerCase();
        },
    },
    {
        displayKey: 'notEqual',
        displayName: 'Does not equal',
        predicate: (filterValue: Nullable<string>, cellValue: CellValue) => {
            if (!filterValue) return true;
            return filterValue.toLowerCase() !== (cellValue ?? '').toString().toLowerCase();
        },
    },
    {
        displayKey: 'startsWith',
        displayName: 'Starts with',
        predicate: (filterValue: Nullable<string>, cellValue: CellValue) => {
            if (!filterValue) return true;
            return (cellValue ?? '').toString().toLowerCase().startsWith(filterValue.toLowerCase());
        },
    },
    {
        displayKey: 'endsWith',
        displayName: 'Ends with',
        predicate: (filterValue: Nullable<string>, cellValue: CellValue) => {
            if (!filterValue) return true;
            return (cellValue ?? '').toString().toLowerCase().endsWith(filterValue.toLowerCase());
        },
    },
    {
        displayKey: 'blank',
        displayName: 'Blank',
        predicate: (_filterValue: Nullable<string>, cellValue: CellValue) => {
            return (typeof cellValue !== 'number' && !cellValue) || cellValue.toString().trim() === '';
        },
    },
    {
        displayKey: 'notBlank',
        displayName: 'Not blank',
        predicate: (_filterValue: Nullable<string>, cellValue: CellValue) => {
            return cellValue !== null && cellValue !== undefined && cellValue.toString().trim() !== '';
        },
    },
    {
        displayKey: 'equalsAny',
        displayName: 'Equals any of',
        predicate: (filterValue: Nullable<string>, cellValue: CellValue) => {
            if (filterValue === null || filterValue === undefined) return true;
            const filters = filterValue
                .split(/\n+/)
                .map((v) => v.trim())
                .filter((v) => v.length > 0);
            return filters.some(
                (filter: string) => (cellValue ?? '').toString().toLowerCase() === filter.toLowerCase()
            );
        },
    },
    {
        displayKey: 'containsAny',
        displayName: 'Contains any of',
        predicate: (filterValue: Nullable<string>, cellValue: CellValue) => {
            if (!filterValue) return true;
            const filters = filterValue
                .split(/\n+/)
                .map((v) => v.trim())
                .filter((v) => v.length > 0);
            return filters.some((filter: string) =>
                (cellValue ?? '').toString().toLowerCase().includes(filter.toLowerCase())
            );
        },
    },
] as const;

type FilterType = (typeof allFilters)[number]['displayKey'];

export interface AdvancedTextColumnFilterModel {
    filterType: FilterType;
    filterValue: Nullable<string>;
    operator: 'AND' | 'OR';
    filterType2: FilterType;
    filterValue2: Nullable<string>;
}

export const AdvancedTextColumnFilter: FC<
    CustomFilterDisplayProps<unknown, unknown, Nullable<AdvancedTextColumnFilterModel>>
> = ({ onStateChange, state, onAction }) => {
    const filterType = state.model?.filterType ?? allFilters[0].displayKey;
    const filterValue = state.model?.filterValue ?? '';
    const operator = state.model?.operator ?? 'AND';
    const filterType2 = state.model?.filterType2 ?? allFilters[0].displayKey;
    const filterValue2 = state.model?.filterValue2 ?? '';

    const hasTextField = useMemo(() => {
        return !['blank', 'notBlank'].includes(filterType);
    }, [filterType]);

    const showSecondCondition = hasTextField && filterValue !== '';

    const hasTextField2 = useMemo(() => {
        return !['blank', 'notBlank'].includes(filterType2);
    }, [filterType2]);

    // biome-ignore lint/correctness/useExhaustiveDependencies: the filter should also be applied when filterType changes
    useEffect(() => {
        const isFilterActive = !hasTextField || (hasTextField && filterValue !== '');

        // If the filter is not active, do not apply it
        if (!isFilterActive) {
            return;
        }

        // Otherwise apply the display state
        onAction('apply');
    }, [filterValue, filterType, filterValue2, filterType2, operator, hasTextField, onAction]);

    const isMultiline = useMemo(() => {
        return ['equalsAny', 'containsAny'].includes(filterType);
    }, [filterType]);

    const isMultiline2 = useMemo(() => {
        return ['equalsAny', 'containsAny'].includes(filterType2);
    }, [filterType2]);

    const updateModel = (patch: Partial<AdvancedTextColumnFilterModel>) => {
        onStateChange({
            model: {
                filterType,
                filterValue,
                operator,
                filterType2,
                filterValue2,
                ...patch,
            },
        });
    };

    return (
        <Stack
            spacing={2}
            sx={{
                p: 2,
            }}
        >
            <Select
                value={filterType}
                onChange={(e) => updateModel({ filterType: e.target.value as FilterType })}
                native
            >
                {allFilters.map((filter) => (
                    <option key={filter.displayKey} value={filter.displayKey}>
                        {filter.displayName}
                    </option>
                ))}
            </Select>
            {hasTextField && (
                <TextField
                    minRows={1}
                    maxRows={10}
                    multiline={isMultiline}
                    value={filterValue ?? ''}
                    onChange={({ target: { value } }) => updateModel({ filterValue: value })}
                />
            )}
            {showSecondCondition && (
                <>
                    <RadioGroup
                        row
                        value={operator}
                        onChange={(_e, value) => updateModel({ operator: value as 'AND' | 'OR' })}
                        sx={{ alignSelf: 'center', gap: 2 }}
                    >
                        <FormControlLabel
                            value="AND"
                            control={<Radio size="small" sx={{ p: 0 }} />}
                            label="AND"
                            sx={{ m: 0, gap: 0.75 }}
                        />
                        <FormControlLabel
                            value="OR"
                            control={<Radio size="small" sx={{ p: 0 }} />}
                            label="OR"
                            sx={{ m: 0, gap: 0.75 }}
                        />
                    </RadioGroup>
                    <Select
                        value={filterType2}
                        onChange={(e) => updateModel({ filterType2: e.target.value as FilterType })}
                        native
                    >
                        {allFilters.map((filter) => (
                            <option key={filter.displayKey} value={filter.displayKey}>
                                {filter.displayName}
                            </option>
                        ))}
                    </Select>
                    {hasTextField2 && (
                        <TextField
                            minRows={1}
                            maxRows={10}
                            multiline={isMultiline2}
                            value={filterValue2 ?? ''}
                            onChange={({ target: { value } }) => updateModel({ filterValue2: value })}
                        />
                    )}
                </>
            )}
        </Stack>
    );
};

export const doesFilterPass = ({
    model,
    node,
    handlerParams,
}: DoesFilterPassParams<unknown, unknown, Nullable<AdvancedTextColumnFilterModel>>) => {
    if (!model) return true; // If no model, pass the filter
    const { getValue } = handlerParams;
    const value = getValue(node);

    const filterPredicate = allFilters.find((filter) => filter.displayKey === model.filterType)?.predicate;
    if (!filterPredicate) return true; // If no predicate found, pass the filter
    const result1 = filterPredicate(model.filterValue, value);

    const hasTextField1 = !['blank', 'notBlank'].includes(model.filterType);
    const showSecondCondition =
        hasTextField1 && model.filterValue !== null && model.filterValue !== undefined && model.filterValue !== '';
    if (!showSecondCondition) return result1;

    const hasTextField2 = !['blank', 'notBlank'].includes(model.filterType2);
    const condition2Active =
        !hasTextField2 ||
        (model.filterValue2 !== null && model.filterValue2 !== undefined && model.filterValue2 !== '');

    if (!condition2Active) return result1;

    const filterPredicate2 = allFilters.find((filter) => filter.displayKey === model.filterType2)?.predicate;
    if (!filterPredicate2) return result1;
    const result2 = filterPredicate2(model.filterValue2, value);

    return model.operator === 'OR' ? result1 || result2 : result1 && result2;
};

export const advancedTextColumnFilterOptions: Pick<ColDef, 'filter' | 'filterParams'> = {
    filter: {
        component: AdvancedTextColumnFilter,
        doesFilterPass,
    },
    filterParams: {
        buttons: ['reset'],
    },
};
